import { useHotkeys } from 'react-hotkeys-hook';
import type { PageCommand } from 'v5/services/front/PageCommandsService/PageCommandsService';

/** Controls that read letters themselves: typeahead in a select, a menu, a list. */
const TAKES_LETTERS =
  '[role="combobox"], [role="listbox"], [role="option"], [role="menu"], [role="menuitem"]';
/**
 * Anything modal over the page, the legacy react-modal ones included (they
 * have no `data-state`). Third-party widgets keep hidden ones mounted (the
 * Featurebase chat), so only a shown one counts.
 */
const OVER_THE_PAGE =
  '[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]:not([data-state="closed"])';

/**
 * Runs each page command on its hotkey (`PageCommandsService`). A page's
 * hotkey may be a plain letter, so it stays off while the user types in a
 * field or a control that reads letters has the focus, and while a dialog
 * or popover is open over the page.
 */
export function PageCommandHotkeys({
  commands
}: {
  commands: ReadonlyArray<PageCommand>;
}) {
  return commands.map(command => (
    <PageCommandHotkey key={command.id} command={command} />
  ));
}

function PageCommandHotkey({ command }: { command: PageCommand }) {
  useHotkeys(command.hotkey, event => {
    const target = event.target;
    if (
      event.defaultPrevented ||
      (target instanceof Element && target.closest(TAKES_LETTERS)) ||
      [...document.querySelectorAll(OVER_THE_PAGE)].some(
        dialog => getComputedStyle(dialog).visibility !== 'hidden'
      )
    ) {
      return;
    }
    event.preventDefault();
    command.run();
  });
  return null;
}
